import api from "@flatfile/api";
import FlatfileListener, { FlatfileEvent } from "@flatfile/listener";
import {
  FlatfileProvider,
  Space,
  useFlatfile,
  useListener
} from "@flatfile/react";
import {
  useIsDesktop,
  WSAlert,
  WSButton,
  WSDataItem,
  WSFlexBox,
  WSGrid,
  WSIcon,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  Batch,
  BatchStatus,
  BatchSummaryExpand
} from "@wingspanhq/wingspan-api-sdk";
import { queriesBatches } from "@query";
import { WSQueryCache } from "@ws-react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useHistory, useLocation } from "react-router-dom";

import { WSQueries } from "../../../../query/WSQuery";
import { IS_PRODUCTION_ENV } from "../../../../shared/constants/environment";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { useDownloadPayableExampleTemplate } from "../../components/DownloadPayableExampleTemplateButton";
import { ImportBatchEventHistoryWidget } from "../../components/ImportBatchEventHistoryWidget";
import { ImportBatchStatusAlert } from "../../components/ImportBatchStatusAlert";
import { ImportBatchSteps } from "../../components/ImportBatchSteps";
import { useModalEditImportBatchName } from "../../components/ModalEditImportBatchName";
import {
  BULK_IMPORT_PAYABLES_IMPORT_NAMESPACE,
  PAYABLES_IMPORT_ACTION_SLUG
} from "../../constants";
import { useImportBatchActions } from "../../hooks/useImportBatchActions";
import { useImportSpaceMetadata } from "../../hooks/useImportSpaceMetadata";
import {
  useGoToImportPayablesBatches,
  routeHelpersImportPayablesBatch,
  routeHelpersImportPayablesBatchHistory
} from "../../paths";
import { selectorImportBatchAge } from "../../selectors/selectorImportBatchAge";
import { selectorImportBatchNames } from "../../selectors/selectorImportBatchNames";
import { selectorIsImportBatchUploadInProgress } from "../../selectors/selectorIsImportBatchUploadInProgress";
import { selectorIsOldImportBatch } from "../../selectors/selectorIsOldImportBatch";
import { flatfileProviderConfig } from "../../utils/flatfileThemeConfig";
import {
  PAYABLE_BATCH_DELETE_DESCRIPTION,
  PAYABLE_BATCH_DELETE_EMPTY_DESCRIPTION,
  PAYABLE_ENTITY_LABEL,
  PAYABLE_ENTITY_LABEL_PLURAL,
  PAYABLE_ENTITY_LABEL_PLURAL_LOWER
} from "./constants";
import { PayableBatchDetailsWidget } from "./PayableBatchDetailsWidget";
import { PayableBatchStatusWidget } from "./PayableBatchStatusWidget";
import { useDownloadPayableImportErrorsCSV } from "./useDownloadPayableImportErrorsCSV";

import styles from "./styles.module.scss";

const PayableBatchDetails: React.FC<{
  batch: Batch;
  openPortalOnMount?: boolean;
}> = ({ batch, openPortalOnMount }) => {
  const batchId = batch.id as string;
  const history = useHistory();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const [showOldBatchAlert, setShowOldBatchAlert] = useState(true);
  const exampleTemplateCSVLinkRef = useRef<any>(null);
  const errorsCSVLinkRef = useRef<any>(null);

  const goToPayableBatches = useGoToImportPayablesBatches();
  const goToHistory = routeHelpersImportPayablesBatchHistory.useGoTo();
  const modalEditName = useModalEditImportBatchName();

  const querySummary = queriesBatches.getBatchSummary.useQuery(
    { batchId, expand: [BatchSummaryExpand.BREAKDOWN] },
    { refetchOnMount: true }
  );

  const spaceMetadata = useImportSpaceMetadata(batchId);
  const { csvProps: exampleTemplateCSVProps } =
    useDownloadPayableExampleTemplate();
  const { errorsCSVFileProps } = useDownloadPayableImportErrorsCSV(batchId);

  const downloadExampleCSV = () => {
    exampleTemplateCSVLinkRef.current?.link?.click();
  };
  const downloadErrorsCSV = () => {
    errorsCSVLinkRef.current?.link?.click();
  };

  // The listener uploads the rows server-side, so nothing here invalidates the
  // batch reads — refresh them when the portal closes, as the v1 route did.
  const onCloseFlatfileModal = () => {
    WSQueryCache.invalidateQueries(queriesBatches.listBatches.queryKeyBase);
    WSQueryCache.invalidateQueries(queriesBatches.listBatchItems.queryKeyBase);
    WSQueryCache.invalidateQueries(queriesBatches.getBatch.queryKeyBase);
    WSQueryCache.invalidateQueries(queriesBatches.getBatchSummary.queryKeyBase);
  };
  const { openPortal, closePortal } = useFlatfile({
    onClose: onCloseFlatfileModal
  });

  const spaceProps = {
    // Reopen the listener's space instead of creating a new one each time.
    ...(batch.metadata?.flatfileBatchId && {
      id: batch.metadata.flatfileBatchId
    }),
    config: {
      name: "Payables Upload",
      namespace: BULK_IMPORT_PAYABLES_IMPORT_NAMESPACE,
      metadata: spaceMetadata
    }
  };

  useListener((listener: FlatfileListener) => {
    listener.on("space:created", (event: FlatfileEvent) => {
      const { spaceId } = event.context;
      console.log("spaceId", spaceId);
    });

    listener.on(
      "job:outcome-acknowledged",
      { job: `workbook:${PAYABLES_IMPORT_ACTION_SLUG}` },
      async (event: FlatfileEvent) => {
        const { spaceId } = event.context;
        console.log(
          `[job:outcome-acknowledged]:workbook:${PAYABLES_IMPORT_ACTION_SLUG} -- spaceId`,
          spaceId
        );

        const space = await api.spaces.get(spaceId);

        // Close the Flatfile portal if there are no rejections.
        const { data: workbookSheets } = await api.sheets.list({
          workbookId: space.data.metadata?.wsInternalWorkbookId
        });
        const rejectionSheet = workbookSheets.find(sheet =>
          sheet.slug.includes("rejections")
        );
        if (rejectionSheet?.recordCounts?.total === 0) {
          closePortal();
        }
      }
    );
  });

  // The start screen hands off with `showBatchCreatedModal` once the batch has
  // been created, so the upload is the next thing the user needs.
  useEffect(() => {
    if (openPortalOnMount) {
      // Drop the hand-off flag from history state, or every reload and every
      // back-navigation to this batch reopens the portal.
      history.replace(location.pathname);
      openPortal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const progress = querySummary.data?.progress;
  const itemCount = progress?.total;
  const isEmptyBatch = itemCount === 0;
  const notes = batch.notes;

  const { batchName, batchFileName } = selectorImportBatchNames({
    batch,
    entityLabel: PAYABLE_ENTITY_LABEL
  });

  const actions = useImportBatchActions({
    batch,
    isEmptyBatch,
    failedCount: progress?.failed,
    entityLabel: PAYABLE_ENTITY_LABEL,
    entityLabelPlural: PAYABLE_ENTITY_LABEL_PLURAL_LOWER,
    deleteDescription: PAYABLE_BATCH_DELETE_DESCRIPTION,
    deleteEmptyBatchDescription: PAYABLE_BATCH_DELETE_EMPTY_DESCRIPTION,
    downloadExampleCSV,
    downloadErrorsCSV,
    onDeleted: () => goToPayableBatches()
  });
  // The failed-imports download belongs to the status widget, not the toolbar.
  const toolbarActions = useMemo(
    () => actions.filter(action => action.name !== "downloadErrorsCSV"),
    [actions]
  );

  const onEditName = () => modalEditName.open({ batch });

  const eventHistoryProps = {
    batch,
    itemCount,
    completedCount: progress?.completed,
    failedCount: progress?.failed,
    entityLabel: PAYABLE_ENTITY_LABEL,
    entityLabelPlural: PAYABLE_ENTITY_LABEL_PLURAL,
    onSeeMore: () => goToHistory({ batchId })
  };

  return (
    <WSPage>
      {/* The payablesImport listener configures the workbook of this space. */}
      <Space {...spaceProps} />
      <CSVLink
        ref={exampleTemplateCSVLinkRef}
        className={styles.hiddenButton}
        {...exampleTemplateCSVProps}
      />
      <CSVLink
        ref={errorsCSVLinkRef}
        className={styles.hiddenButton}
        {...errorsCSVFileProps}
      />

      {/*
        Every widget below reads the row counts, and an unknown count is not the
        same as zero: a batch with no rows yet would read as "upload finished"
        for the first frame and offer "Import payables" before flipping to the
        empty state. Hold the page until the summary lands, as the v1 route did.
        The Flatfile `Space` stays outside so the portal can still be opened on
        arrival from the start screen.
      */}
      <WSQueries queries={{ querySummary }}>
        {({ querySummaryData: batchSummary }) => (
          <>
            <WSPageToolbar
              breadcrumbs={[
                {
                  label: "Bulk imports (payables)",
                  onClick: () => goToPayableBatches()
                },
                { label: batchName }
              ]}
              actions={toolbarActions}
            />

            <WSFlexBox.CenterY mb="S">
              <WSText.Heading3 weight="medium" mr="L">
                {batchName}
              </WSText.Heading3>
              <WSButton.Link onClick={onEditName}>Edit name</WSButton.Link>
            </WSFlexBox.CenterY>

            {batchFileName && (
              <WSFlexBox.CenterY>
                <WSIcon name="file" mr="XS" size="XS" />
                <WSText.ParagraphXs weight="book" color="gray600">
                  {batchFileName}
                </WSText.ParagraphXs>
              </WSFlexBox.CenterY>
            )}

            <WSGrid gutter="XL" my="2XL">
              <WSGrid.Item span={{ s: "12", m: "8" }}>
                <WSList>
                  {!isDesktop && (
                    <ImportBatchSteps
                      batch={batch}
                      isEmptyBatch={isEmptyBatch}
                      failedCount={progress?.failed}
                    />
                  )}

                  {notes && (
                    <WSPanel>
                      <WSDataItem
                        label={{
                          text: "Notes",
                          action: { label: "Edit", onClick: onEditName }
                        }}
                        value={notes}
                      />
                    </WSPanel>
                  )}

                  {showOldBatchAlert &&
                    batch.status === BatchStatus.CREATED &&
                    selectorIsOldImportBatch(batch) && (
                      <WSAlert
                        theme="info"
                        icon="alert-circle"
                        title={`This upload is over ${selectorImportBatchAge(
                          batch
                        )} old`}
                        onDismiss={() => setShowOldBatchAlert(false)}
                      >
                        This batch contains an upload over{" "}
                        {selectorImportBatchAge(batch)} old that was not yet
                        imported and may contain old validation data from the
                        time it was uploaded. To ensure up-to-date validation
                        for your formatted spreadsheet, delete this batch and
                        start a new one to upload and import your spreadsheet.
                        We recommend completing the import as soon as your
                        spreadsheet is finished uploading so it doesn’t sit for
                        an extended period of time.
                      </WSAlert>
                    )}

                  <ImportBatchStatusAlert
                    batch={batch}
                    itemCount={itemCount}
                    completedCount={progress?.completed}
                    failedCount={progress?.failed}
                  />

                  <PayableBatchStatusWidget
                    batch={batch}
                    batchSummary={batchSummary}
                    downloadExampleCSV={downloadExampleCSV}
                    downloadErrorsCSV={downloadErrorsCSV}
                    onDeleted={() => goToPayableBatches()}
                  />

                  <PayableBatchDetailsWidget
                    batch={batch}
                    batchSummary={batchSummary}
                  />

                  {!isDesktop && (
                    <ImportBatchEventHistoryWidget {...eventHistoryProps} />
                  )}
                </WSList>
              </WSGrid.Item>

              <WSGrid.Item span={{ s: "12", m: "4" }}>
                {isDesktop && (
                  <WSList gap="L">
                    <ImportBatchSteps
                      batch={batch}
                      isEmptyBatch={isEmptyBatch}
                      failedCount={progress?.failed}
                    />
                    <ImportBatchEventHistoryWidget {...eventHistoryProps} />
                  </WSList>
                )}
              </WSGrid.Item>
            </WSGrid>
          </>
        )}
      </WSQueries>
    </WSPage>
  );
};

export const RouteImportPayableBatchDetails: React.FC = () => {
  useBrowserPageTitle("Bulk imports: Payable batch");
  const { params, state } = routeHelpersImportPayablesBatch.useData();
  const queryBatch = queriesBatches.getBatch.useQuery(
    { batchId: params.batchId },
    { refetchOnMount: true }
  );

  return (
    <WSQueries queries={{ queryBatch }}>
      {({ queryBatchData: batch }) => {
        const ffProviderProps = batch.metadata?.flatfileAccessToken
          ? { accessToken: batch.metadata.flatfileAccessToken }
          : {
              publishableKey: process.env
                .REACT_APP_FLATFILE_PUBLISHABLE_KEY as string
            };

        return (
          <FlatfileProvider
            // Do not set key prop if upload is in progress
            {...(selectorIsImportBatchUploadInProgress(batch)
              ? {}
              : {
                  key:
                    ffProviderProps.accessToken ??
                    ffProviderProps.publishableKey
                })}
            config={{
              debug: !IS_PRODUCTION_ENV,
              ...flatfileProviderConfig
            }}
            {...ffProviderProps}
          >
            <PayableBatchDetails
              batch={batch}
              openPortalOnMount={state?.showBatchCreatedModal}
            />
          </FlatfileProvider>
        );
      }}
    </WSQueries>
  );
};
